import { useEffect, useId, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Switch } from '@/components/ui/Switch';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { ProfileCard } from '@/components/common/ProfileCard';
import { useEmailPreferences } from './hooks/useEmailPreferences';
import { useUpdateEmailPreferences } from './hooks/useUpdateEmailPreferences';

const S = UI_STRINGS.EMAIL_PREFERENCES;

/**
 * Static, read-only list of the mail that is sent whatever the toggle says, so
 * optional and transactional email are visibly distinct (FR-164). Rendered
 * without waiting on the server: it is true for every member in every state.
 */
function AlwaysSentList() {
  return (
    <section aria-labelledby="email-always-sent-heading" className="flex flex-col gap-2 lg:flex-1">
      <h3 id="email-always-sent-heading" className="text-navy text-sm font-semibold">
        {S.ALWAYS_HEADING}
      </h3>
      <p className="text-slate m-0 text-sm">{S.ALWAYS_INTRO}</p>
      <ul className="text-navy m-0 list-disc pl-5 text-sm sm:columns-2">
        {S.ALWAYS_ITEMS.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

/**
 * "Email preferences" card on the account page (FR-164, MYN-120).
 *
 * Reads and writes through its own query and mutation, so it renders on its own
 * schedule rather than waiting for the profile. The switch is NOT optimistic: it
 * is disabled while a save is in flight and always shows the value the server
 * last returned, so a failed save visibly leaves the previous choice in place
 * and offers a retry.
 */
export function EmailPreferencesCard() {
  const preferences = useEmailPreferences();
  const update = useUpdateEmailPreferences();
  const switchRef = useRef<HTMLButtonElement>(null);
  // Set when this card's own control starts a save. A natively disabled switch
  // drops focus (Chrome, Firefox), so focus is handed back once the save settles.
  const restoreFocus = useRef(false);
  const hintId = useId();
  const errorId = useId();
  const isSaving = update.isPending;

  useEffect(() => {
    if (isSaving || !restoreFocus.current) return;
    restoreFocus.current = false;
    switchRef.current?.focus();
  }, [isSaving]);

  function save(optedOut: boolean) {
    restoreFocus.current = true;
    update.mutate(optedOut);
  }
  // Lead with the reassurance that nothing changed, then the API's own reason
  // when it supplied one (ui-guidelines.md section 12).
  const saveReason = update.isError ? getApiErrorMessage(update.error, '') : '';
  const saveErrorText = saveReason ? `${S.SAVE_ERROR} ${saveReason}` : S.SAVE_ERROR;

  return (
    <ProfileCard title={S.SECTION_TITLE}>
      {/* Side by side from `lg`, so the card stays short: the toggle on the left, the
          always-sent list on the right. Stacked on smaller screens. */}
      <div className="flex flex-col gap-5 lg:flex-row lg:gap-8">
        <div className="flex flex-col gap-3 lg:flex-1">
          {preferences.isPending && <LoadingSpinner size="sm" label={S.LOADING} />}

          {preferences.isError && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p role="alert" className="text-danger m-0 text-sm">
                {getApiErrorMessage(preferences.error, S.LOAD_ERROR)}
              </p>
              <Button
                variant="link"
                className="shrink-0 font-semibold"
                onClick={() => void preferences.refetch()}
              >
                {S.RETRY}
              </Button>
            </div>
          )}

          {preferences.data && (
            <div className="flex flex-col gap-3">
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <p className="text-slate text-[11px] font-semibold tracking-[0.08em] uppercase">
                    {S.TOGGLE_LABEL}
                  </p>
                  <p className="text-navy m-0 text-sm font-medium">
                    {preferences.data.optedOut ? S.STATUS_OFF : S.STATUS_ON}
                  </p>
                  <p id={hintId} className="text-slate m-0 text-sm">
                    {S.TOGGLE_HINT}
                  </p>
                </div>
                <Switch
                  ref={switchRef}
                  label={S.TOGGLE_LABEL}
                  checked={!preferences.data.optedOut}
                  disabled={isSaving}
                  aria-describedby={update.isError ? `${hintId} ${errorId}` : hintId}
                  onCheckedChange={(receive) => save(!receive)}
                />
              </div>

              {update.isPending && (
                <p role="status" className="text-slate m-0 text-sm">
                  {S.SAVING}
                </p>
              )}

              {update.isError && (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p id={errorId} role="alert" className="text-danger m-0 text-sm">
                    {saveErrorText}
                  </p>
                  <Button
                    variant="link"
                    className="shrink-0 font-semibold"
                    onClick={() => update.variables !== undefined && save(update.variables)}
                  >
                    {S.RETRY}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-divider h-px w-full lg:h-auto lg:w-px lg:self-stretch" />
        <AlwaysSentList />
      </div>
    </ProfileCard>
  );
}
